import type { ClientSite } from "@/lib/types";

export const MAX_SITE_CODE_ARCHIVE_BYTES = 500 * 1024 * 1024;

export interface SiteDomainOption {
  value: string;
  label: string;
  kind: "production" | "preview";
}

function hostnameFromURL(value?: string) {
  if (!value?.trim()) return "";
  try {
    return new URL(value).hostname.toLowerCase();
  } catch {
    return "";
  }
}

export function getSiteDomainOptions(site: ClientSite): SiteDomainOption[] {
  const options: SiteDomainOption[] = [];
  const seen = new Set<string>();
  const add = (value: string, label: string, kind: SiteDomainOption["kind"]) => {
    const normalized = value.trim().toLowerCase();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    options.push({ value: normalized, label, kind });
  };

  if (site.domain) {
    add(site.domain, site.domain, "production");
  }

  const previewDomain = hostnameFromURL(site.preview_url);
  if (previewDomain) {
    add(previewDomain, `${previewDomain} · предпросмотр`, "preview");
  } else if (site.preview_slug) {
    const fallbackPreviewDomain = `${site.preview_slug.trim().toLowerCase()}.wenlix.ru`;
    add(fallbackPreviewDomain, `${fallbackPreviewDomain} · предпросмотр`, "preview");
  }

  return options;
}

export function canUpdateSiteCode(site: ClientSite) {
  return Boolean(site.domain?.trim() && site.cms_site_id) && site.deploy_status !== "none";
}

export function formatArchiveSize(bytes: number) {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 100 * 1024 * 1024 ? 0 : 1)} МБ`;
}

export function validateSiteCodeArchive(file: File) {
  if (!file.name.toLowerCase().endsWith(".zip")) {
    return "Поддерживаются только ZIP-архивы.";
  }
  if (file.size === 0) {
    return "Архив пустой.";
  }
  if (file.size > MAX_SITE_CODE_ARCHIVE_BYTES) {
    return "Архив больше 500 МБ.";
  }
  return null;
}

export function siteCodeUploadErrorMessage(status: number, archiveBytes: number) {
  if (status !== 413) return "";
  if (archiveBytes > MAX_SITE_CODE_ARCHIVE_BYTES) {
    return "Архив больше 500 МБ.";
  }
  return `Сервер отклонил архив из-за лимита загрузки. Размер файла — ${formatArchiveSize(archiveBytes)}; допустимо до 500 МБ. Production-сайт не изменён.`;
}

interface CodePublicationState {
  active_release_id?: string | null;
  published_config_version: number;
  publication_pipeline_enabled: boolean;
  active_publication_id?: string | null;
}

export function isLegacyCodePublication(preflight: CodePublicationState | undefined) {
  return preflight?.active_release_id === null && Number.isInteger(preflight.published_config_version) && preflight.published_config_version >= 0;
}

export function codePublicationMode(preflight: CodePublicationState | undefined, legacyAccepted = false): "standard" | "legacy" | null {
  if (!preflight?.publication_pipeline_enabled || preflight.active_publication_id) return null;
  if (legacyAccepted) return isLegacyCodePublication(preflight) ? "legacy" : null;
  return preflight.active_release_id ? "standard" : null;
}
